import { KeyboardEvent, RefObject, useEffect, useRef, useState } from 'react';
import { hasFocusOutsideList } from './utils/hasFocusOutsideList';

type UseListFocusNavArgs = {
  // Container whose descendant rows match rowSelector; used for both reading current focus and re-focusing on index change.
  listRef: RefObject<HTMLElement | null>;
  itemsLength: number;
  // Selector for focusable rows in DOM order; defaults to ion-item with tabindex 0.
  rowSelector?: string;
  // Called when ArrowDown is pressed on the last row — caller may trigger lazy load.
  onLoadMore?: () => void;
  // Called when ArrowUp is pressed on the first row — caller may return focus to a parent (e.g. search input).
  onLeaveTop?: () => void;
  // Called when Space is pressed; receives the focused index (only fires when handler is provided).
  onSpace?: (index: number) => void;
  // Called when Enter is pressed; receives the focused index (only fires when handler is provided).
  onEnter?: (index: number) => void;
  // Called when Escape is pressed (only fires when handler is provided).
  onEscape?: () => void;
};

const DEFAULT_ROW_SELECTOR = 'ion-item[tabindex="0"]';

// Encapsulates state-driven keyboard navigation for a popover list — arrow nav, Space/Enter/Escape, DOM-order focus sync.
export const useListFocusNav = ({
  listRef,
  itemsLength,
  rowSelector = DEFAULT_ROW_SELECTOR,
  onLoadMore,
  onLeaveTop,
  onSpace,
  onEnter,
  onEscape,
}: UseListFocusNavArgs) => {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  // Refs, not state: pure bookkeeping for the focus-sync effect that must never cause a render.
  const lastFocusedRowRef = useRef<HTMLElement | null>(null);
  const lastItemsLengthRef = useRef<number>(itemsLength);
  const lastFocusedIndexRef = useRef<number | null>(null);

  const getRows = () =>
    Array.from(
      listRef.current?.querySelectorAll<HTMLElement>(rowSelector) ?? []
    );

  // Looks up the focused row's DOM-order position so state stays correct even if the row's React index differs from DOM index.
  const setFocusedFromEvent = (
    event: { currentTarget: HTMLElement },
    fallbackIndex: number
  ) => {
    const domIndex = getRows().indexOf(event.currentTarget);
    setFocusedIndex(domIndex >= 0 ? domIndex : fallbackIndex);
  };

  // Delegated keyboard handler for the list container; relies on focusedIndex state instead of document.activeElement to survive shadow-DOM quirks. Handled keys also stopPropagation so window-level shortcuts (e.g. quick filter trigger reacting to Space) don't see them.
  const onContainerKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (focusedIndex === null) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (focusedIndex >= itemsLength - 1) {
        onLoadMore?.();
        return;
      }
      setFocusedIndex(focusedIndex + 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      if (focusedIndex === 0) {
        setFocusedIndex(null);
        onLeaveTop?.();
        return;
      }
      setFocusedIndex(focusedIndex - 1);
      return;
    }
    if (event.key === ' ' && onSpace) {
      event.preventDefault();
      event.stopPropagation();
      onSpace(focusedIndex);
      return;
    }
    if (event.key === 'Enter' && onEnter) {
      event.preventDefault();
      event.stopPropagation();
      onEnter(focusedIndex);
      return;
    }
    if (event.key === 'Escape' && onEscape) {
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    }
  };

  // Syncs DOM focus to React-managed focusedIndex; runs after items update so the target row exists in the DOM.
  useEffect(() => {
    const appendedPage = itemsLength > lastItemsLengthRef.current;
    const indexChanged: boolean = focusedIndex !== lastFocusedIndexRef.current;
    lastItemsLengthRef.current = itemsLength;
    lastFocusedIndexRef.current = focusedIndex;

    if (focusedIndex === null) {
      lastFocusedRowRef.current = null;
      return;
    }
    const target = getRows()[focusedIndex];
    // A lazy page re-runs this via itemsLength while the focused row survives untouched below the append, so the only thing left to do would be scrolling it back into view.
    if (
      appendedPage &&
      target &&
      target === lastFocusedRowRef.current &&
      target.contains(document.activeElement)
    ) {
      return;
    }
    if (!indexChanged) {
      // Only the row count moved: reclaim focus the list itself lost, and scroll only when the node was replaced instead of kept in place.
      const rowSurvived: boolean = target === lastFocusedRowRef.current;
      lastFocusedRowRef.current = target ?? null;
      if (hasFocusOutsideList(listRef.current)) return;
      target?.focus({ preventScroll: true });
      if (!rowSurvived) target?.scrollIntoView({ block: 'nearest' });
      return;
    }
    lastFocusedRowRef.current = target ?? null;
    target?.focus();
    target?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, itemsLength]);

  return {
    focusedIndex,
    setFocusedIndex,
    setFocusedFromEvent,
    onContainerKeyDown,
  };
};
